import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useWallet } from "@/context/WalletContext";
import { usePrice } from "@/components/croc/hooks";
import WalletButton from "@/components/croc/WalletButton";
import { fmtNum } from "@/lib/format";
import api from "@/lib/api";
import { ShieldCheck, Flame, TrendingUp, Lock, ArrowRight, Coins, Users, Trophy, Vault, ExternalLink, Vote, Landmark, Radio, Target, Zap } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/f4559a2d-ea78-4441-aaab-378b8fe74f14/images/0f42e3e4fd29d6c5d61cbf5b61d3e038619e02d3642878d5eaaa5928fc903e3e.jpeg";
const LOGO = "/logo.png";

const FEATURES = [
  { icon: Lock, t: "5 Staking Tiers", d: "24h to 720d weighting multipliers, dynamic daily rate capped at 0.5%." },
  { icon: Users, t: "Referral Engine", d: "5% direct sponsor bonus, 9 ranks, 21 override levels — revenue funded." },
  { icon: Trophy, t: "Croc Pool", d: "5% of turnover funds one pool — King #1 wins 20%, Baby #2 wins 10% daily, 70% carries forward." },
  { icon: Flame, t: "Burn on Sell", d: "Withdrawals pay USDT at live oracle rate and burn CROC supply." },
];

const DAO_FEATURES = [
  { icon: Vote, t: "DAO Governance", d: "CROC holders steer the protocol — voting on rates, fees, treasury spend and new pools. Every parameter change is proposed and ratified transparently." },
  { icon: Landmark, t: "Community Treasury", d: "A DAO-governed treasury accumulates protocol fees in the open. Holders decide how reserves fund growth, buybacks and rewards." },
];

const PRED_FEATURES = [
  { icon: Radio, t: "Live Polymarket Feed", d: "Real prediction markets streamed from Polymarket every hour — fresh, tier-matched questions daily, never stale data." },
  { icon: Target, t: "Trustless On-Chain Payouts", d: "Winners are credited by the PredictionMarket contract at settlement — no manual payouts, every result provable on BscScan." },
  { icon: Zap, t: "Daily Prediction Bonus", d: "Answer every daily prediction and your day's staking reward doubles — the bonus lands straight in your claimable balance." },
];

export default function Landing() {
  const { address } = useWallet();
  const navigate = useNavigate();
  const price = usePrice();
  const [reserves, setReserves] = useState(null);

  useEffect(() => {
    const load = () => api.chainReserves().then(setReserves).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen">
      {/* nav */}
      <div className="glass border-b border-white/10 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-5 h-16">
          <div className="flex items-center gap-2.5">
            <img src={LOGO} alt="CROC" className="w-9 h-9 rounded-lg object-cover" />
            <div className="leading-none">
              <div className="font-display font-black text-lg text-white">CROC</div>
              <div className="text-[9px] uppercase tracking-[0.28em] gold-text">PROTOCOL</div>
            </div>
          </div>
          {address ? (
            <button
              data-testid="enter-dapp-button"
              onClick={() => navigate("/overview")}
              className="flex items-center gap-2 rounded-xl bg-[#1CFD4C] px-4 py-2.5 font-semibold text-[#050806] hover:bg-[#4DFF74] transition-colors"
            >
              Enter dApp <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <WalletButton />
          )}
        </div>
      </div>

      {/* hero */}
      <section className="relative overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050806]/60 via-[#050806]/80 to-[#050806]" />
        <div className="relative max-w-6xl mx-auto px-5 py-20 sm:py-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#1CFD4C]/40 bg-[#0F1712]/70 px-3 py-1.5 mb-6 glow-gold">
              <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] live-dot" />
              <ShieldCheck className="w-3.5 h-3.5 gold-text" />
              <span className="text-xs text-muted-foreground">BEP-20 · 1B Hard Cap · Revenue-Backed Yield</span>
            </div>
            <h1 className="font-display font-black text-4xl sm:text-6xl leading-[1.05] tracking-tight text-white max-w-3xl">
              The apex predator of <span className="gold-text" style={{ textShadow: "0 0 30px rgba(212,175,55,0.4)" }}>DeFi staking</span> on BNB Chain.
            </h1>
            <p className="text-muted-foreground text-lg mt-5 max-w-xl leading-relaxed">
              Buy & auto-stake CROC at the live oracle rate. Earn dynamic, revenue-funded yield across 5 tiers, a 9-rank referral engine, and daily reward pools — never diluting the fixed supply.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              {address ? (
                <button
                  data-testid="hero-enter-button"
                  onClick={() => navigate("/overview")}
                  className="flex items-center gap-2 rounded-xl bg-[#1CFD4C] px-6 py-3.5 font-semibold text-[#050806] hover:bg-[#4DFF74] transition-colors glow-gold"
                >
                  Launch Command Center <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <WalletButton />
              )}
              <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#0F1712]/70 px-4 py-3">
                <TrendingUp className="w-5 h-5 emerald-text" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">CROC/USDT</div>
                  <div className="font-num font-bold text-white">${fmtNum(price?.price, 4)}
                    <span className={`ml-2 text-xs ${(price?.change_24h ?? 0) >= 0 ? "tick-up" : "tick-down"}`}>
                      {fmtNum(price?.change_24h, 2)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* features */}
      <section className="max-w-6xl mx-auto px-5 pb-20 -mt-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.t}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="croc-panel p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#1CFD4C]/30"
            >
              <div className={`w-11 h-11 rounded-xl grid place-items-center mb-4 border ${i % 2 === 0 ? "bg-[#1CFD4C]/12 border-[#1CFD4C]/35" : "bg-[#10B981]/12 border-[#10B981]/30"}`}>
                <f.icon className={`w-5 h-5 ${i % 2 === 0 ? "gold-text" : "emerald-text"}`} />
              </div>
              <div className="font-display font-bold text-white text-lg">{f.t}</div>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>

        {/* DAO protocol + Prediction market */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <div className="text-xs uppercase tracking-[0.3em] gold-text mb-2">Governance &amp; Markets</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-white">DAO-governed. <span className="gold-text">Prediction-powered.</span></h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mb-5">
            {DAO_FEATURES.map((f, i) => (
              <motion.div key={f.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="croc-panel p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#1CFD4C]/30" data-testid={`dao-section-${i}`}>
                <div className="w-11 h-11 rounded-xl grid place-items-center mb-4 border bg-[#1CFD4C]/12 border-[#1CFD4C]/35">
                  <f.icon className="w-5 h-5 gold-text" />
                </div>
                <div className="font-display font-bold text-white text-lg">{f.t}</div>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.d}</p>
              </motion.div>
            ))}
          </div>

          <div className="grid sm:grid-cols-3 gap-5">
            {PRED_FEATURES.map((f, i) => (
              <motion.div key={f.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="croc-panel p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#10B981]/30" data-testid={`prediction-section-${i}`}>
                <div className="w-11 h-11 rounded-xl grid place-items-center mb-4 border bg-[#10B981]/12 border-[#10B981]/30">
                  <f.icon className="w-5 h-5 emerald-text" />
                </div>
                <div className="font-display font-bold text-white text-lg">{f.t}</div>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.d}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* proof of reserves — live on-chain trust signal */}
        {reserves?.available && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="croc-panel p-6 mt-6 glow-emerald shimmer-border"
            data-testid="proof-of-reserves"
          >
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#1CFD4C]/12 border border-[#1CFD4C]/35 grid place-items-center glow-gold">
                  <Vault className="w-5 h-5 gold-text" />
                </div>
                <div>
                  <div className="font-display font-bold text-white text-lg flex items-center gap-2">
                    Proof of Reserves
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-[#39FF14]/40 bg-[#39FF14]/10 px-2 py-0.5 text-[10px] text-[#50FA7B]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] live-dot" /> Live on-chain
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">RevenuePool balance read straight from BNB Chain — rewards are revenue-backed, never from principal.</div>
                </div>
              </div>
              <a
                href={reserves.explorer}
                target="_blank"
                rel="noreferrer"
                data-testid="reserves-explorer-link"
                className="inline-flex items-center gap-1.5 text-sm emerald-text hover:underline font-num"
              >
                <ExternalLink className="w-3.5 h-3.5" /> View on BscScan
              </a>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <ReserveStat label="Revenue Reserve" value={`$${fmtNum(reserves.reserve_usd, 2)}`} sub={`${fmtNum(reserves.reserve_bnb, 4)} BNB`} data-testid="reserve-usd" />
              <ReserveStat label="Coverage Ratio" value={`${fmtNum(reserves.coverage_ratio, 1)}×`} sub="of daily obligations" accent data-testid="reserve-coverage" />
              <ReserveStat label="Total Received" value={`${fmtNum(reserves.total_received_bnb, 4)} BNB`} sub="lifetime into pool" />
              <ReserveStat label="TVL Secured" value={`$${fmtNum(reserves.tvl_usd, 2)}`} sub="staked value on-chain" />
            </div>
          </motion.div>
        )}

      </section>
    </div>
  );
}

function ReserveStat({ label, value, sub, accent, ...rest }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#090E0B] px-4 py-4" {...rest}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-num font-bold text-xl mt-1 ${accent ? "gold-text" : "text-white"}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
    </div>
  );
}
